import React, { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { TrophyFilled } from "@ant-design/icons";
import Nav from "../landing-pages/home-two/components/nav";
import { MTN_YELLOW } from "../../constants/Colors";
import { useHideEbanqoWidget } from "../../hooks/useHideEbanqoWidget";
import GamesService, {
  type LeaderboardEntry,
  type LeaderboardPeriod,
} from "../../services/games";
import { useAppStore } from "../../store";
import { getAxiosErrorUserMessage } from "../../utils/apiErrorMessage";

/**
 * The public board. Shows board names and scores only — no profile, no
 * phone, no real names unless a player typed one as their alias.
 *
 * "This week" is a rolling seven days server-side, so it stays winnable for
 * someone who finds the game on a Friday.
 */
/** One board per game. The game arrives as ?game= so the games can link
 *  straight to their own board. */
const GAMES: Array<{ slug: string; label: string }> = [
  { slug: "kae-kente", label: "Kae Kente" },
  { slug: "dose-dash", label: "Dose Dash" },
  { slug: "makola-plate", label: "Makola" },
];

const PERIODS: Array<{ key: LeaderboardPeriod; label: string }> = [
  { key: "week", label: "This week" },
  { key: "all", label: "All time" },
];

/** The board is keyed by score slug; the routes do not all match, so the map
 *  is explicit rather than a string template. */
const ROUTE_FOR: Record<string, string> = {
  "kae-kente": "/play/kae-kente",
  "dose-dash": "/play/dose-dash",
  "makola-plate": "/play/makola",
};

/** What each game's peak column means. Same column in the database, two
 *  different things, so the board has to say which. */
/** Under this many names the board leaves a lot of empty card on a phone.
 *  Rather than a blank panel, that space asks them to fill it. */
const SHORT_BOARD = 8;

/** How often an open board re-reads itself. Ten seconds reads as live to
 *  someone watching and costs one small query. */
const BOARD_POLL_MS = 10_000;

const PEAK_LABEL: Record<string, string> = {
  "kae-kente": "bands",
  "dose-dash": "days",
  "makola-plate": "rounds",
};

export default function Leaderboard() {
  useHideEbanqoWidget("games-leaderboard");
  const { loggedIn } = useAppStore();
  const [searchParams, setSearchParams] = useSearchParams();

  const requested = searchParams.get("game");
  const game = GAMES.some((entry) => entry.slug === requested)
    ? (requested as string)
    : GAMES[0].slug;

  const [period, setPeriod] = useState<LeaderboardPeriod>("week");
  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [myRank, setMyRank] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (
      which: LeaderboardPeriod,
      forGame: string,
      { quiet = false }: { quiet?: boolean } = {},
    ) => {
      // A quiet pass is a background refresh: no spinner, and a failure leaves
      // the board that is already on screen alone rather than replacing it
      // with an error. Someone reading the board should never see it blink.
      if (!quiet) {
        setLoading(true);
        setError(null);
      }
      try {
        const board = await GamesService.leaderboard({
          game: forGame,
          period: which,
        });
        setEntries(board);
      } catch (err) {
        if (!quiet) setError(getAxiosErrorUserMessage(err));
      } finally {
        if (!quiet) setLoading(false);
      }

      // A signed-in player's own place, fetched separately so a failure here
      // never costs them the board itself.
      if (!loggedIn) {
        setMyRank(null);
        return;
      }
      try {
        const mine = await GamesService.myRank({ game: forGame, period: which });
        setMyRank(mine.rank);
      } catch {
        setMyRank(null);
      }
    },
    [loggedIn],
  );

  useEffect(() => {
    void load(period, game);
  }, [load, period, game]);

  /**
   * Keeps an open board current. Runs take about a minute, so this is not a
   * race — the point is that a board someone is watching should not be a
   * snapshot from when they opened it.
   *
   * A hidden tab does not poll: browsers throttle background timers anyway,
   * and refreshing a board nobody is looking at is pure noise. Becoming
   * visible again refreshes immediately rather than waiting out the interval.
   */
  useEffect(() => {
    const refresh = () => void load(period, game, { quiet: true });

    let timer = window.setInterval(refresh, BOARD_POLL_MS);

    const onVisibilityChange = () => {
      window.clearInterval(timer);
      if (document.hidden) return;
      refresh();
      timer = window.setInterval(refresh, BOARD_POLL_MS);
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [load, period, game]);

  return (
    <div className="flex min-h-screen w-full flex-col bg-neutral-950 text-white">
      <div className="text-black">
        <Nav />
      </div>

      <main className="mx-auto flex w-full max-w-md flex-1 flex-col px-4 py-6 sm:max-w-xl md:max-w-2xl lg:max-w-3xl">
        <div className="flex items-baseline justify-between gap-3">
          <h1 className="m-0 flex items-center gap-2 text-xl font-bold sm:text-3xl">
            <TrophyFilled style={{ color: MTN_YELLOW }} />
            Leaderboard
          </h1>
          <Link to={ROUTE_FOR[game] ?? "/play"} className="text-xs text-white/60 underline">
            Play
          </Link>
        </div>

        {/* Which game's board. */}
        <div className="mt-4 flex gap-2">
          {GAMES.map((option) => (
            <button
              key={option.slug}
              type="button"
              onClick={() => setSearchParams({ game: option.slug })}
              className={[
                "rounded-lg px-3 py-1.5 text-xs font-semibold sm:px-4 sm:py-2.5 sm:text-base",
                game === option.slug
                  ? "bg-white/20 text-white"
                  : "bg-white/5 text-white/50 hover:bg-white/10",
              ].join(" ")}
            >
              {option.label}
            </button>
          ))}
        </div>

        <div className="mt-2 flex gap-2">
          {PERIODS.map((option) => (
            <button
              key={option.key}
              type="button"
              onClick={() => setPeriod(option.key)}
              className={[
                "rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors sm:px-4 sm:py-2.5 sm:text-base",
                period === option.key
                  ? "text-black"
                  : "bg-white/10 text-white/70 hover:bg-white/20",
              ].join(" ")}
              style={
                period === option.key ? { backgroundColor: MTN_YELLOW } : undefined
              }
            >
              {option.label}
            </button>
          ))}
        </div>

        {myRank !== null && (
          <p className="mt-3 text-xs text-white/60">
            You&apos;re {myRank}
            {ordinal(myRank)} {period === "week" ? "this week" : "all time"}.
          </p>
        )}

        <div className="mt-4 flex min-h-0 max-h-[36rem] flex-1 flex-col overflow-hidden rounded-2xl bg-neutral-900 ring-1 ring-white/10">
          {loading ? (
            <p className="m-0 flex flex-1 items-center justify-center px-4 py-8 text-center text-xs text-white/40">
              Loading the board…
            </p>
          ) : error ? (
            <div className="flex flex-1 flex-col items-center justify-center px-4 py-8 text-center">
              <p className="m-0 text-xs text-white/60">{error}</p>
              <button
                type="button"
                onClick={() => void load(period, game)}
                className="mt-2 text-xs text-white/80 underline"
              >
                Try again
              </button>
            </div>
          ) : entries.length === 0 ? (
            <div className="flex flex-1 flex-col items-center justify-center px-5 py-8 text-center">
              <p className="m-0 text-sm text-white/70">
                Nobody on the board yet
                {period === "week" ? " this week" : ""}.
              </p>
              <Link
                to={ROUTE_FOR[game] ?? "/play"}
                className="mt-3 inline-block rounded-lg px-4 py-2 text-sm font-semibold text-black"
                style={{ backgroundColor: MTN_YELLOW }}
              >
                Be the first
              </Link>
            </div>
          ) : (
            <>
              <ul className="m-0 min-h-0 list-none divide-y divide-white/5 overflow-y-auto p-0">
                {entries.map((entry) => (
                  <li
                    key={`${entry.rank}-${entry.alias}-${entry.achievedAt}`}
                    className="flex items-center gap-3 px-4 py-3 sm:px-5 sm:py-4"
                  >
                    <span
                      className={[
                        "w-7 shrink-0 text-center text-sm font-bold tabular-nums sm:w-10 sm:text-lg",
                        entry.rank <= 3 ? "" : "text-white/40",
                      ].join(" ")}
                      style={entry.rank <= 3 ? { color: MTN_YELLOW } : undefined}
                    >
                      {entry.rank}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-sm sm:text-lg">
                      {entry.alias}
                    </span>
                    <span className="shrink-0 text-right">
                      <span className="block text-sm font-semibold tabular-nums sm:text-lg">
                        {entry.score}
                      </span>
                      <span className="block text-[10px] text-white/40 sm:text-xs">
                        {entry.bestSpan} {PEAK_LABEL[game] ?? ""}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
              {entries.length < SHORT_BOARD && (
                <div className="flex flex-1 shrink-0 flex-col items-center justify-center gap-2.5 px-5 py-8 text-center">
                  <p className="m-0 text-xs leading-relaxed text-white/40">
                    Room for more.{" "}
                    {period === "week"
                      ? "This week's board is still open."
                      : "Every run counts."}
                  </p>
                  <Link
                    to={ROUTE_FOR[game] ?? "/play"}
                    className="rounded-lg px-4 py-2 text-sm font-semibold text-black"
                    style={{ backgroundColor: MTN_YELLOW }}
                  >
                    Play {GAMES.find((entry) => entry.slug === game)?.label}
                  </Link>
                </div>
              )}
            </>
          )}
        </div>

        <div className="mt-auto flex items-center justify-between pt-5 text-[11px] text-white/35">
          <Link to="/play" className="underline">
            All games
          </Link>
          <span>Board names and scores only</span>
        </div>
      </main>
    </div>
  );
}

/** 1st, 2nd, 3rd, 4th — including the 11th/12th/13th exceptions. */
function ordinal(n: number): string {
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return "th";
  switch (n % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
}
